import { ChevronDownIcon, MonitorIcon, SearchIcon, SquareStackIcon } from "lucide-react";
import { useState, type FocusEvent, type KeyboardEvent, type ReactElement } from "react";

import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { ProjectChoiceRow } from "./FirstAccountSetupParts";
import {
  isSetupProjectOpenable,
  type SetupProjectRow,
  type SetupProjectWorkspaceOption,
} from "./firstAccountSetupData";

const ALL_WORKSPACES_FILTER = "__all__";

interface ProjectWorkspaceFilterOption {
  readonly id: string;
  readonly label: string;
  readonly iconUrl: string | null;
  readonly count: number;
}

function matchesProjectSearch(project: SetupProjectRow, query: string): boolean {
  if (query.length === 0) {
    return true;
  }

  const haystack = [
    project.name,
    project.cwd,
    project.environmentLabel,
    project.defaultProviderLabel,
    project.githubRepoUrl ?? "",
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
}

function projectWorkspaceOptions(
  projects: readonly SetupProjectRow[],
  configuredOptions: readonly SetupProjectWorkspaceOption[],
): readonly ProjectWorkspaceFilterOption[] {
  const optionsById = new Map<string, ProjectWorkspaceFilterOption>();
  for (const option of configuredOptions) {
    optionsById.set(option.id, {
      id: option.id,
      label: option.label,
      iconUrl: option.iconUrl,
      count: 0,
    });
  }

  for (const project of projects) {
    const existing = optionsById.get(project.environmentId);
    optionsById.set(project.environmentId, {
      id: project.environmentId,
      label: project.environmentLabel,
      iconUrl: existing?.iconUrl ?? project.environmentIconUrl ?? null,
      count: (existing?.count ?? 0) + 1,
    });
  }

  return Array.from(optionsById.values()).toSorted((a, b) => a.label.localeCompare(b.label));
}

function filteredProjectRows(input: {
  readonly projects: readonly SetupProjectRow[];
  readonly searchQuery: string;
  readonly workspaceFilter: string;
}): readonly SetupProjectRow[] {
  const normalizedQuery = input.searchQuery.trim().toLowerCase();
  return input.projects.filter((project) => {
    const matchesWorkspace =
      input.workspaceFilter === ALL_WORKSPACES_FILTER ||
      project.environmentId === input.workspaceFilter;
    return matchesWorkspace && matchesProjectSearch(project, normalizedQuery);
  });
}

function WorkspaceMark(props: {
  readonly label: string;
  readonly iconUrl: string | null;
  readonly kind?: "all" | "workspace";
}): ReactElement {
  const fallback = props.label.trim().charAt(0).toUpperCase() || "W";
  return props.iconUrl ? (
    <img
      alt=""
      className="size-5 rounded-md border border-border object-cover"
      src={props.iconUrl}
    />
  ) : props.kind === "all" ? (
    <span className="flex size-5 items-center justify-center rounded-md border border-border bg-muted text-muted-foreground">
      <SquareStackIcon aria-hidden="true" className="size-3.5" />
    </span>
  ) : props.label.toLowerCase().includes("macbook") || props.label === "Local" ? (
    <span className="flex size-5 items-center justify-center rounded-md border border-border bg-muted text-muted-foreground">
      <MonitorIcon aria-hidden="true" className="size-3.5" />
    </span>
  ) : (
    <span className="flex size-5 items-center justify-center rounded-md border border-border bg-muted text-[10px] font-semibold text-muted-foreground">
      {fallback}
    </span>
  );
}

function WorkspaceFilterOptionRow(props: {
  readonly option: ProjectWorkspaceFilterOption;
  readonly selected: boolean;
  readonly onSelect: (value: string) => void;
}): ReactElement {
  return (
    <button
      type="button"
      role="option"
      aria-selected={props.selected}
      className={
        props.selected
          ? "flex h-10 w-full items-center gap-3 bg-accent px-3 text-left text-sm font-medium text-foreground outline-none"
          : "flex h-10 w-full items-center gap-3 px-3 text-left text-sm font-medium text-foreground outline-none transition-colors hover:bg-accent/80 focus:bg-accent/80"
      }
      onClick={() => props.onSelect(props.option.id)}
    >
      <WorkspaceMark
        label={props.option.label}
        iconUrl={props.option.iconUrl}
        kind={props.option.id === ALL_WORKSPACES_FILTER ? "all" : "workspace"}
      />
      <span className="min-w-0 flex-1 truncate">{props.option.label}</span>
      <span className="text-xs text-muted-foreground">{props.option.count}</span>
    </button>
  );
}

function WorkspaceFilterDropdown(props: {
  readonly workspaceFilter: string;
  readonly workspaceOptions: readonly ProjectWorkspaceFilterOption[];
  readonly totalCount: number;
  readonly onWorkspaceFilterChange: (value: string) => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const allWorkspacesOption: ProjectWorkspaceFilterOption = {
    id: ALL_WORKSPACES_FILTER,
    label: "All projects",
    iconUrl: null,
    count: props.totalCount,
  };
  const options = [allWorkspacesOption, ...props.workspaceOptions];
  const selectedWorkspace =
    options.find((option) => option.id === props.workspaceFilter) ?? allWorkspacesOption;

  const handleSelect = (value: string) => {
    props.onWorkspaceFilterChange(value);
    setOpen(false);
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    const nextFocus = event.relatedTarget;
    const currentTarget = event.currentTarget;
    if (nextFocus instanceof Node && currentTarget.contains(nextFocus)) {
      return;
    }
    setOpen(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape") {
      return;
    }
    setOpen(false);
  };

  return (
    <div className="relative" onBlur={handleBlur} onKeyDown={handleKeyDown}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Filter by project"
        className={
          open
            ? "flex h-9 w-full items-center gap-3 rounded-md border border-primary bg-background px-3 text-left text-sm font-medium text-foreground shadow-[0_0_0_3px_hsl(var(--primary)/0.12)] outline-none"
            : "flex h-9 w-full items-center gap-3 rounded-md border border-border bg-background px-3 text-left text-sm font-medium text-foreground outline-none transition-colors hover:border-primary/70 focus:border-primary focus:shadow-[0_0_0_3px_hsl(var(--primary)/0.12)]"
        }
        onClick={() => setOpen((current) => !current)}
      >
        <WorkspaceMark
          label={selectedWorkspace.label}
          iconUrl={selectedWorkspace.iconUrl}
          kind={selectedWorkspace.id === ALL_WORKSPACES_FILTER ? "all" : "workspace"}
        />
        <span className="min-w-0 flex-1 truncate">{selectedWorkspace.label}</span>
        <ChevronDownIcon
          aria-hidden="true"
          className={
            open
              ? "size-4 shrink-0 rotate-180 text-muted-foreground transition-transform"
              : "size-4 shrink-0 text-muted-foreground transition-transform"
          }
        />
      </button>
      {open ? (
        <div
          role="listbox"
          aria-label="Project options"
          className="absolute top-11 left-0 z-30 max-h-72 w-full min-w-80 overflow-auto rounded-lg border border-border bg-popover py-1 text-popover-foreground shadow-xl"
        >
          {options.map((option) => (
            <WorkspaceFilterOptionRow
              key={option.id}
              option={option}
              selected={option.id === selectedWorkspace.id}
              onSelect={handleSelect}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ProjectSearchAndWorkspaceFilter(props: {
  readonly searchQuery: string;
  readonly workspaceFilter: string;
  readonly workspaceOptions: readonly ProjectWorkspaceFilterOption[];
  readonly totalCount: number;
  readonly filteredCount: number;
  readonly onSearchQueryChange: (value: string) => void;
  readonly onWorkspaceFilterChange: (value: string) => void;
}): ReactElement {
  return (
    <div className="rounded-lg border border-border bg-background/70 p-3">
      <div className="grid gap-3 sm:grid-cols-[minmax(13rem,0.42fr)_minmax(0,1fr)_auto] sm:items-center">
        <WorkspaceFilterDropdown
          workspaceFilter={props.workspaceFilter}
          workspaceOptions={props.workspaceOptions}
          totalCount={props.totalCount}
          onWorkspaceFilterChange={props.onWorkspaceFilterChange}
        />
        <label className="relative block">
          <SearchIcon
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            value={props.searchQuery}
            placeholder="Search workspaces and projects"
            className="h-9 w-full rounded-md border border-border bg-background pr-3 pl-9 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary"
            onChange={(event) => props.onSearchQueryChange(event.currentTarget.value)}
          />
        </label>
        <span className="text-xs text-muted-foreground">
          {props.filteredCount} of {props.totalCount}
        </span>
      </div>
    </div>
  );
}

function ProjectFilterEmptyState(props: {
  readonly onOpenProjectImporter: (() => void) | undefined;
}): ReactElement {
  return (
    <div className="rounded-lg border border-border bg-background/70 p-4">
      <h3 className="text-sm font-semibold text-foreground">
        No workspaces or projects match this filter
      </h3>
      <p className="mt-2 text-sm leading-5 text-muted-foreground">
        Try a different project or search term, or import a project from GitHub.
      </p>
      <Button
        className="mt-4"
        type="button"
        variant="secondary"
        disabled={!props.onOpenProjectImporter}
        onClick={() => props.onOpenProjectImporter?.()}
      >
        Open project importer
      </Button>
    </div>
  );
}

function ProjectChoiceRowSkeleton(props: { readonly index: number }): ReactElement {
  const widthClass = props.index === 0 ? "w-40" : props.index === 1 ? "w-52" : "w-36";
  return (
    <div
      aria-hidden="true"
      className="grid grid-cols-[1.75rem_minmax(0,1fr)_auto] items-start gap-3 rounded-lg border border-border bg-background/70 p-4"
    >
      <Skeleton className="mt-0.5 size-7 rounded-md" />
      <div className="grid min-w-0 gap-2">
        <Skeleton className={`h-4 ${widthClass}`} />
        <Skeleton className="h-3 w-full max-w-lg" />
        <Skeleton className="h-3 w-56" />
      </div>
      <Skeleton className="h-7 w-20 rounded-md" />
    </div>
  );
}

function FirstAccountSetupProjectPanelSkeleton(): ReactElement {
  return (
    <div className="mt-6 grid gap-4" aria-label="Loading workspaces and projects">
      <div className="rounded-lg border border-border bg-background/70 p-3">
        <div className="grid gap-3 sm:grid-cols-[minmax(13rem,0.42fr)_minmax(0,1fr)_auto] sm:items-center">
          <Skeleton className="h-9 rounded-md" />
          <Skeleton className="h-9 rounded-md" />
          <Skeleton className="h-4 w-14" />
        </div>
      </div>
      <section className="grid gap-3">
        <div>
          <Skeleton className="h-4 w-28" />
          <Skeleton className="mt-2 h-3 w-80 max-w-full" />
        </div>
        {[0, 1, 2].map((index) => (
          <ProjectChoiceRowSkeleton key={index} index={index} />
        ))}
      </section>
    </div>
  );
}

export function FirstAccountSetupProjectPanel(props: {
  readonly projects: readonly SetupProjectRow[];
  readonly projectWorkspaceOptions: readonly SetupProjectWorkspaceOption[];
  readonly projectsLoading: boolean;
  readonly selectedProjectId: string | null;
  readonly onProjectChange: (projectId: string) => void;
  readonly onOpenProjectImporter: ((project?: SetupProjectRow) => void) | undefined;
}): ReactElement {
  const [searchQuery, setSearchQuery] = useState("");
  const [workspaceFilter, setWorkspaceFilter] = useState(ALL_WORKSPACES_FILTER);
  const workspaceOptions = projectWorkspaceOptions(props.projects, props.projectWorkspaceOptions);
  const visibleProjects = filteredProjectRows({
    projects: props.projects,
    searchQuery,
    workspaceFilter,
  });
  const openableProjects = visibleProjects.filter(isSetupProjectOpenable);
  const unavailableForgeProjects = visibleProjects.filter(
    (project) => project.source === "forge" && !isSetupProjectOpenable(project),
  );
  const cmmdProjects = visibleProjects.filter((project) => project.source === "cmmd");
  const hasVisibleProjects = visibleProjects.length > 0;
  if (props.projectsLoading && props.projects.length === 0) {
    return <FirstAccountSetupProjectPanelSkeleton />;
  }

  if (props.projects.length === 0) {
    return (
      <div className="mt-6 rounded-lg border border-border bg-background/70 p-4">
        <h3 className="text-sm font-semibold text-foreground">No Forge projects loaded</h3>
        <p className="mt-2 text-sm leading-5 text-muted-foreground">
          Open the project importer to clone from GitHub, bring in CMMD work, or start from a new
          folder.
        </p>
        <Button
          className="mt-4"
          type="button"
          variant="secondary"
          disabled={!props.onOpenProjectImporter}
          onClick={() => props.onOpenProjectImporter?.()}
        >
          Open project importer
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-6 grid gap-4">
      <ProjectSearchAndWorkspaceFilter
        searchQuery={searchQuery}
        workspaceFilter={workspaceFilter}
        workspaceOptions={workspaceOptions}
        totalCount={props.projects.length}
        filteredCount={visibleProjects.length}
        onSearchQueryChange={setSearchQuery}
        onWorkspaceFilterChange={setWorkspaceFilter}
      />
      {!hasVisibleProjects ? (
        <ProjectFilterEmptyState
          onOpenProjectImporter={
            props.onOpenProjectImporter ? () => props.onOpenProjectImporter?.() : undefined
          }
        />
      ) : null}
      {openableProjects.length > 0 ? (
        <section className="grid gap-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Ready to open</h3>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              Existing Forge projects can start a chat now, whether they are local or cloud.
            </p>
          </div>
          {openableProjects.map((project) => (
            <ProjectChoiceRow
              key={project.id}
              project={project}
              selected={props.selectedProjectId === project.id}
              onSelect={props.onProjectChange}
              {...(props.onOpenProjectImporter
                ? { onPrepareProject: props.onOpenProjectImporter }
                : {})}
            />
          ))}
        </section>
      ) : null}
      {unavailableForgeProjects.length > 0 ? (
        <section className="grid gap-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Needs attention</h3>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              These Forge projects are saved, but the local path is not available on this device.
            </p>
          </div>
          {unavailableForgeProjects.map((project) => (
            <ProjectChoiceRow
              key={project.id}
              project={project}
              selected={false}
              onSelect={props.onProjectChange}
              {...(props.onOpenProjectImporter
                ? { onPrepareProject: props.onOpenProjectImporter }
                : {})}
            />
          ))}
        </section>
      ) : null}
      {cmmdProjects.length > 0 ? (
        <section className="rounded-lg border border-border bg-background/70">
          <div className="border-b border-border px-4 py-3">
            <h3 className="text-sm font-semibold text-foreground">CMMD projects</h3>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              CMMD projects are planning context. Import or link a repository before agents can run
              on them.
            </p>
          </div>
          <div className="grid gap-2 p-3">
            {cmmdProjects.map((project) => (
              <ProjectChoiceRow
                key={project.id}
                project={project}
                selected={false}
                onSelect={props.onProjectChange}
                {...(props.onOpenProjectImporter
                  ? { onPrepareProject: props.onOpenProjectImporter }
                  : {})}
              />
            ))}
          </div>
          <div className="border-t border-border px-4 py-3">
            <Button
              type="button"
              variant="secondary"
              disabled={!props.onOpenProjectImporter}
              onClick={() => props.onOpenProjectImporter?.()}
            >
              Open importer
            </Button>
          </div>
        </section>
      ) : null}
    </div>
  );
}

export function FirstAccountSetupReadyPanel(props: {
  readonly projects: readonly SetupProjectRow[];
  readonly selectedProjectId: string | null;
  readonly selectedProviderLabel: string;
}): ReactElement {
  const selectedProject =
    props.projects.find((project) => project.id === props.selectedProjectId) ?? null;
  const hasCmmdOnlyProjects =
    selectedProject === null && props.projects.some((project) => project.source === "cmmd");
  return (
    <div className="mt-6 rounded-lg border border-border bg-background/70 p-4">
      <h3 className="text-sm font-semibold text-foreground">Ready for your first chat</h3>
      <p className="mt-2 max-w-xl text-sm leading-5 text-muted-foreground">
        {selectedProject
          ? `Save setup and open ${selectedProject.name} with ${props.selectedProviderLabel}.`
          : hasCmmdOnlyProjects
            ? "Save setup now. Use the project importer to turn a CMMD planning project into a runnable local or cloud project."
            : "Save setup now. Import a project before starting an agent chat."}
      </p>
    </div>
  );
}
